import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Modal, Select, Switch, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../lib/api';
import { editorApi, rolesAdminApi, type BroadcastInput } from '../lib/wikiApi';

/**
 * «Разослать» (фаза 1.3): из опубликованной статьи создаётся CRM-новость с
 * лидом и ссылкой (или полным телом) и уходит в TG/MAX через модуль
 * «Новости» — категорийные чаты, личные рассылки партнёрам, сотрудникам
 * через employee-бот. Таргетинг — тот же, что в композере CRM.
 */
export function BroadcastModal({
  open,
  onClose,
  articleId,
  slug,
}: {
  open: boolean;
  onClose: () => void;
  articleId: string;
  slug: string;
}): JSX.Element {
  const qc = useQueryClient();
  const targets = useQuery({ queryKey: ['wiki', 'broadcast-targets'], queryFn: () => editorApi.broadcastTargets(), enabled: open, staleTime: 60_000 });
  const roles = useQuery({ queryKey: ['wiki', 'roles-for-visibility'], queryFn: () => rolesAdminApi.list(), enabled: open, retry: false, staleTime: 5 * 60_000 });

  const [form, setForm] = useState<BroadcastInput>({ categoryId: '', priority: 'NORMAL', broadcastTelegram: true, broadcastMax: true, forEmployees: false, fullBody: false });
  useEffect(() => {
    if (open) setForm({ categoryId: '', priority: 'NORMAL', broadcastTelegram: true, broadcastMax: true, forEmployees: false, fullBody: false });
  }, [open]);
  useEffect(() => {
    // Первая категория по умолчанию.
    if (open && !form.categoryId && targets.data?.categories[0]) setForm((f) => ({ ...f, categoryId: targets.data!.categories[0].id }));
  }, [open, targets.data, form.categoryId]);

  const mut = useMutation({
    mutationFn: () => editorApi.broadcast(articleId, form),
    onSuccess: async (r) => {
      message.success(r.status === 'PUBLISHED' ? 'Разослано: новость создана и ушла в каналы' : 'Новость создана и ждёт модерации');
      await qc.invalidateQueries({ queryKey: ['wiki', 'article', slug] });
      onClose();
    },
    onError: (e: Error) => {
      const body = e instanceof ApiError ? (e.body as { message?: string } | undefined) : undefined;
      message.error(body?.message ?? e.message);
    },
  });

  const t = targets.data;
  const cat = t?.categories.find((c) => c.id === form.categoryId);
  const set = <K extends keyof BroadcastInput>(k: K, v: BroadcastInput[K]): void => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => mut.mutate()}
      okText="Разослать"
      cancelText="Отмена"
      okButtonProps={{ disabled: !form.categoryId, loading: mut.isPending }}
      title="Разослать статью"
      width={640}
      destroyOnHidden
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
        <div style={{ fontSize: 13, color: 'var(--fk-text-2)' }}>
          В CRM появится новость с лидом статьи и ссылкой «Читать в ВикиБрасол», и уйдёт в Telegram/MAX по настройкам категории и адресатам.
        </div>
        <Field label="Категория новостей">
          <Select
            style={{ width: '100%' }}
            value={form.categoryId || undefined}
            onChange={(v) => set('categoryId', v)}
            options={(t?.categories ?? []).map((c) => ({ value: c.id, label: c.requiresModeration ? `${c.name} (с модерацией)` : c.name }))}
            loading={targets.isLoading}
            placeholder="Выберите категорию"
          />
          {cat?.requiresModeration && <Hint>Категория с модерацией: новость уйдёт после подтверждения модератором.</Hint>}
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Приоритет">
            <Select
              style={{ width: '100%' }}
              value={form.priority}
              onChange={(v) => set('priority', v)}
              options={[
                { value: 'NORMAL', label: 'Обычная' },
                { value: 'IMPORTANT', label: 'Важная' },
                { value: 'REQUIRES_ACTION', label: 'Требует действия (перейти по ссылке)' },
              ]}
            />
          </Field>
          <Field label="Дедлайн (для «требует действия»)">
            <input type="date" className="fk-meta-input" value={form.deadline ?? ''} onChange={(e) => set('deadline', e.target.value || null)} disabled={form.priority !== 'REQUIRES_ACTION'} />
          </Field>
        </div>
        <Field label="Роли CRM (пусто — всем)">
          <Select mode="multiple" style={{ width: '100%' }} value={form.forRoles ?? []} onChange={(v) => set('forRoles', v)} options={(roles.data ?? []).map((r) => ({ value: r.code, label: r.name || r.code }))} placeholder="Все роли" allowClear />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Партнёры (личная рассылка)">
            <Select mode="multiple" showSearch optionFilterProp="label" style={{ width: '100%' }} value={form.forPartnerIds ?? []} onChange={(v) => set('forPartnerIds', v)} options={(t?.partners ?? []).map((p) => ({ value: p.id, label: p.name }))} placeholder="Не выбрано" allowClear />
          </Field>
          <Field label="Города">
            <Select mode="multiple" showSearch style={{ width: '100%' }} value={form.forCities ?? []} onChange={(v) => set('forCities', v)} options={(t?.cities ?? []).map((c) => ({ value: c.city, label: `${c.city} (${c.partnerCount})` }))} placeholder="Не выбрано" allowClear />
          </Field>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Switch checked={!!form.forEmployees} onChange={(v) => set('forEmployees', v)} />
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>Сотрудникам точек (через бота сотрудников)</span>
        </div>
        {form.forEmployees && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Должности (пусто — все)">
              <Select mode="multiple" style={{ width: '100%' }} value={form.employeeRoleCodes ?? []} onChange={(v) => set('employeeRoleCodes', v)} options={(t?.employeeRoles ?? []).map((r) => ({ value: r.code, label: r.name }))} placeholder="Все должности" allowClear />
            </Field>
            <Field label="Точки (пусто — вся сеть)">
              <Select mode="multiple" showSearch optionFilterProp="label" style={{ width: '100%' }} value={form.employeeTradingPointIds ?? []} onChange={(v) => set('employeeTradingPointIds', v)} options={(t?.tradingPoints ?? []).map((p) => ({ value: p.id, label: p.label }))} placeholder="Вся сеть" allowClear maxTagCount={3} />
            </Field>
          </div>
        )}
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}><Switch size="small" checked={!!form.broadcastTelegram} onChange={(v) => set('broadcastTelegram', v)} /> Telegram</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}><Switch size="small" checked={!!form.broadcastMax} onChange={(v) => set('broadcastMax', v)} /> MAX</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}><Switch size="small" checked={!!form.fullBody} onChange={(v) => set('fullBody', v)} /> Отправить текст целиком, а не лид со ссылкой</label>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--fk-text-muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }): JSX.Element {
  return <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginTop: 6 }}>{children}</div>;
}
